import {
    Component,
    Input,
    type OnDestroy,
    type OnInit,
    inject,
} from "@angular/core";
import { HeaderComponent } from "@coreui/angular";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { EMPTY, type Observable, Subject } from "rxjs";
import { catchError, startWith, switchMap, takeUntil } from "rxjs/operators";
import { AccountService, type IUser, LoginService } from "../../../core";
import { ClientMessageService } from "../../../entities/message/service/client-message.service";
import { UserAppNotificationDetailModalComponent } from "../../../entities/user-app-notification/component/detail/user-app-notification-detail-modal.component";
import type { IUserAppNotification } from "../../../entities/user-app-notification/model/user-app-notification.model";
import { UserAppNotificationRealtimeService } from "../../../entities/user-app-notification/service/user-app-notification-realtime.service";
import { UserAppNotificationService } from "../../../entities/user-app-notification/service/user-app-notification.service";

@Component({
    selector: "app-default-header",
    templateUrl: "./default-header.component.html",
    styleUrls: ["./default-header.component.scss"],
    standalone: false,
})
export class DefaultHeaderComponent
    extends HeaderComponent
    implements OnInit, OnDestroy
{
    private accountService = inject(AccountService);
    private loginService = inject(LoginService);
    private modalService = inject(NgbModal);
    private clientMessageService = inject(ClientMessageService);
    private userAppNotificationService = inject(UserAppNotificationService);
    private userAppNotificationRealtimeService = inject(
        UserAppNotificationRealtimeService,
    );

    currentAccount: IUser;
    currentUserIsAdmin: boolean;
    currentUserIsLimitedAccess: boolean;
    showClientMessages: boolean;
    unreadMessagesCount$: Observable<number>;
    userAppNotifications: IUserAppNotification[] = [];
    unreadUserAppNotificationsCount: number = 0;

    destroyed$ = new Subject<void>();

    @Input() sidebarId: string = "sidebar";
    @Input() hidenav: boolean;

    ngOnInit(): void {
        this.accountService.userIdentity
            .pipe(takeUntil(this.destroyed$))
            .subscribe(() => this.refreshNav());

        this.userAppNotificationService.onUpdate
            .pipe(
                startWith(undefined),
                switchMap(() =>
                    this.userAppNotificationService
                        .getAll({
                            readFlag: false,
                            inactive: false,
                            page: 0,
                            size: 10,
                            sort: ["createdDate,desc"],
                        })
                        .pipe(catchError(() => EMPTY)),
                ),
                takeUntil(this.destroyed$),
            )
            .subscribe((getAllModel) => {
                this.userAppNotifications = getAllModel.notifications;
                this.unreadUserAppNotificationsCount = getAllModel.totalCount;
            });
        this.userAppNotificationRealtimeService.ensureStarted();
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    refreshNav(): void {
        if (!this.hidenav && this.accountService.hasAuthToken()) {
            this.accountService.identity().then((account) => {
                this.currentAccount = account;
                this.currentUserIsAdmin = this.accountService.hasAnyAuthority([
                    "ROLE_ADMIN",
                ]);
                this.currentUserIsLimitedAccess =
                    this.accountService.hasAnyAuthority([
                        "ROLE_LIMITED_ACCESS",
                    ]);

                if (
                    this.currentAccount.firmSettings.enableClientMessages &&
                    this.accountService.hasAnyAuthority([
                        "ROLE_CLIENT_MESSAGES",
                    ])
                ) {
                    this.showClientMessages = true;
                    this.getUnreadMessagesCount();
                } else {
                    this.showClientMessages = false;
                }
            });
        }
    }

    logout(): void {
        this.loginService.logout();
    }

    openNotificationDetailModal(notification: IUserAppNotification): void {
        const modalRef = this.modalService.open(
            UserAppNotificationDetailModalComponent,
            { size: "lg" },
        );
        modalRef.componentInstance.notification = notification;
    }

    private getUnreadMessagesCount(): void {
        this.unreadMessagesCount$ =
            this.clientMessageService.unreadMessagesCount$;
        this.clientMessageService.getUnreadClientMessagesCount().subscribe();
    }
}
